import { useFormContext } from 'react-hook-form';
import { realisation as rules } from 'further-ui/validations';

import Spacing from 'components/Spacing';
import GridContainer from 'components/GridContainer';
import { CurrencyCode } from 'further-types/lp/vehicle';
import SectionedTable, { Row } from 'components/SectionedTable';
import { HoldingClassRealisations } from '../../types';
import { getManualConsiderationColumns } from './helpers/getManualConsiderationColumns';

const AMOUNT_KEYS: Array<Row> = [
  {
    key: 'unitsSold',
    label: 'Units realised',
    group: 'received',
  },
  {
    key: 'receivedConsiderationTotal',
    label: 'Received consideration',
    group: 'received',
    tooltip:
      'This is the consideration physically received by the fund following the realisation. If there is withholding tax accounted for at the fund level, please do not include it here. Instead, add it in ‘Withholding tax payable’ below.',
  },
  {
    key: 'receivedConsiderationPerUnit',
    label: 'Received consideration per unit',
    group: 'received',
  },
  {
    key: 'escrowConsiderationTotal',
    label: 'Escrow consideration',
    group: 'escrow',
    tooltip:
      'This is the total consideration placed in escrow and not yet received by the fund following the realisation but to which the fund has a future right. Any escrow consideration physically received should be removed from this row and added to the received consideration total.',
  },
  {
    key: 'escrowConsiderationPerUnit',
    label: 'Escrow consideration per unit',
    group: 'escrow',
  },

  {
    key: 'earnoutConsiderationTotal',
    label: 'Earnout consideration',
    group: 'earnout',
    tooltip:
      'This is the visible future consideration that is not yet received by the fund following the realisation but that the fund expects to receive from an earnout or delayed consideration structure. Any earnout consideration actually received should be removed from this row and added to the received consideration total. This field is record-keeping purposes only and will not result in a general ledger entry.',
  },
  {
    key: 'earnoutConsiderationPerUnit',
    label: 'Earnout consideration per unit',
    group: 'earnout',
  },

  {
    key: 'withholdingTaxPayable',
    label: 'Withholding tax payable',
    tooltip:
      'This is the total withholding tax payable by the fund and its limited partners as part of the realisation. All consideration lines above should exclude withholding tax. If withholding tax is the buyer’s responsibility or is paid on the fund’s behalf (and not recorded as a fund-level expense), leave this field empty.',
    group: 'withholdingTaxPayable',
  },
];

type ManualConsiderationFormProps = {
  currencyCode: CurrencyCode;
  disabled: boolean;
};

const ManualConsiderationForm: React.FC<ManualConsiderationFormProps> = ({
  currencyCode,
  disabled,
}) => {
  const { control, getValues } =
    useFormContext<rules.CreateRealisationFormValues>();

  const holdingClassRealisations: HoldingClassRealisations = getValues(
    `holdingClassRealisation`,
  );

  const columns = getManualConsiderationColumns({
    holdingClassRealisations,
    currencyCode,
    control,
    disabled,
  });

  return (
    <GridContainer>
      <Spacing size="md">
        <SectionedTable
          columns={columns}
          rows={AMOUNT_KEYS}
          showSeparator={false}
        />
      </Spacing>
    </GridContainer>
  );
};

export default ManualConsiderationForm;
